import { defineStore } from "pinia";
import type {
  WebModelGetAddress,
  WebModelGetCountry,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import { getAddress } from "~/model/api/pLCnextStoreFrontendAPI";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";

interface State {
  legalAddress: WebModelGetAddress | null;
  legalAddressCountry: WebModelGetCountry | null;
}

export const useCompanyAddressStore = defineStore("companyaddress", {
  // convert to a function
  state: (): State => ({
    legalAddress: null,
    legalAddressCountry: null,
  }),
  getters: {
    legalAddressIsWhitelisted(): boolean {
      return this.legalAddressCountry?.status === "Full";
    },
    legalAddressCanSpecifyPXCNumber(): boolean {
      return ["GB", "NO", "CH"].includes(
        this.legalAddressCountry?.iso as string,
      );
    },
    legalAddressIsInUSHub(): boolean {
      return (
        this.legalAddressCountry?.iso === "US" ||
        this.legalAddressCountry?.iso === "CA" ||
        this.legalAddressCountry?.iso === "MX"
      );
    },
    legalAddressIsCanada(): boolean {
      return this.legalAddressCountry?.iso === "CA";
    },
    legalAddressIsUS(): boolean {
      return this.legalAddressCountry?.iso === "US";
    },
  },
  actions: {
    async loadCompanyLegalAddress(legalAddressId: number) {
      if (!legalAddressId) {
        this.legalAddress = null;
        return;
      }
      const { data, error } = await withErrorHandling(
        withErrorMessage(withLoading(getAddress(legalAddressId))),
      );
      console.debug("loadCompanyLegalAddress: ", data, error);
      if (data) {
        this.legalAddress = data;
      }
    },
    // easily reset state using `$reset`
    clear() {
      this.$reset();
    },
    async loadCompanyLegalAddressCountry(
      legalAddress: WebModelGetAddress | null,
      countries: WebModelGetCountry[],
    ) {
      if (!legalAddress || !legalAddress.country || !countries) {
        return;
      }

      // find in countries array
      const country = countries.find(
        (country) => country.iso === legalAddress.country,
      );
      if (!country) {
        console.debug(
          "Could not find country with iso " +
            legalAddress.country +
            " in countries list, skipping load.",
        );
        return;
      }
      this.legalAddressCountry = country;
    },
  },
});
